export const GoogleMapApiConfig = ""; // Set in Admin Settings -> Google Keys
export const GoogleMapId = ""; // Set in Admin Settings -> Google Keys

/**
 * [GOOGLE_KEYS] Resolver for dynamic Google Settings from DB
 */
export const DEFAULT_SERVICE_BIAS = {
    // Bordeaux Metropole & Gironde / Nouvelle-Aquitaine
    center: { lat: 44.8378, lng: -0.5792 },
    radius: 80000, // 80 km radius around Bordeaux
    north: 45.45,
    south: 44.25,
    east: 0.35,
    west: -1.45,
};

/**
 * [GOOGLE_KEYS] Resolver for dynamic Google Settings from DB
 */
export const getGoogleMapsConfig = (settings) => {
    const google = settings?.google;
    const configuredBias = google?.places?.bias;

    // Dynamic Service Area from Admin General Settings (defaults to Bordeaux + 80km)
    const enforceServiceArea = settings?.enforceServiceArea !== undefined ? Boolean(settings.enforceServiceArea) : true;
    const serviceCityName = (typeof settings?.serviceCityName === "string" && settings.serviceCityName.trim() !== "")
        ? settings.serviceCityName.trim()
        : "Bordeaux, France";
    const parsedLat = parseFloat(settings?.serviceCityLat);
    const parsedLng = parseFloat(settings?.serviceCityLng);
    const centerLat = !isNaN(parsedLat) && parsedLat !== 0 ? parsedLat : DEFAULT_SERVICE_BIAS.center.lat;
    const centerLng = !isNaN(parsedLng) && parsedLng !== 0 ? parsedLng : DEFAULT_SERVICE_BIAS.center.lng;
    const parsedRadiusKm = parseFloat(settings?.serviceAreaRadiusKm);
    const radiusKm = !isNaN(parsedRadiusKm) && parsedRadiusKm > 0 ? parsedRadiusKm : 80;
    const serviceCityCountry = (typeof settings?.serviceCityCountry === "string" && settings.serviceCityCountry.trim() !== "")
        ? settings.serviceCityCountry.trim().toUpperCase()
        : "FR";

    const latDelta = radiusKm / 111.32;
    const cosLat = Math.max(0.1, Math.cos((centerLat * Math.PI) / 180));
    const lngDelta = radiusKm / (111.32 * cosLat);

    const dynamicServiceBias = {
        center: { lat: centerLat, lng: centerLng },
        radius: Math.round(radiusKm * 1000),
        north: Number((centerLat + latDelta).toFixed(4)),
        south: Number((centerLat - latDelta).toFixed(4)),
        east: Number((centerLng + lngDelta).toFixed(4)),
        west: Number((centerLng - lngDelta).toFixed(4)),
    };

    const bias = configuredBias || dynamicServiceBias;

    // Priority: settings.restrictCountry (General Settings) > settings.google.places.countries (Google Keys Settings) > ["FR"]
    let countries = ["FR"];
    if (typeof settings?.restrictCountry === "string" && settings.restrictCountry.trim() !== "") {
        const parsed = settings.restrictCountry
            .split(",")
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);
        if (parsed.length > 0) {
            countries = parsed;
        }
    } else if (Array.isArray(google?.places?.countries) && google.places.countries.length > 0) {
        const parsed = google.places.countries
            .map((c) => String(c).trim().toUpperCase())
            .filter(Boolean);
        if (parsed.length > 0) {
            countries = parsed;
        }
    } else if (typeof google?.places?.countries === "string" && google.places.countries.trim() !== "") {
        const parsed = google.places.countries
            .split(",")
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);
        if (parsed.length > 0) {
            countries = parsed;
        }
    }

    return {
        apiKey: google?.maps?.browserKey || GoogleMapApiConfig || "",
        mapId: google?.maps?.mapId || GoogleMapId || "",
        region: google?.places?.region || "FR",
        languageMode: google?.places?.languageMode || "AUTO",
        countries: countries.slice(0, 15),
        strictBounds: Boolean(google?.places?.strictBounds),
        bias: bias,
        serviceArea: {
            enforceServiceArea,
            cityName: serviceCityName,
            shortCityName: serviceCityName.split(",")[0].trim() || "Bordeaux",
            lat: centerLat,
            lng: centerLng,
            radiusKm,
            country: serviceCityCountry,
            bounds: dynamicServiceBias,
        },
    };
};

/**
 * [GOOGLE_KEYS] Stable Map Language Resolver
 * Prevents "Loader must not be called again with different options" by picking 
 * a language once and sticking to it for the window lifetime.
 */
let globalMapLanguage = null;
export const getStableMapLanguage = (location) => {
    if (globalMapLanguage) return globalMapLanguage;
    if (typeof window === "undefined") return "fr";

    // Priority: query param ?lang=, pathname /en, or default fr
    try {
        const search = window.location.search;
        if (search) {
            const params = new URLSearchParams(search);
            const qLang = params.get("lang");
            if (qLang) {
                const val = qLang.toLowerCase();
                if (val.startsWith("en")) {
                    globalMapLanguage = "en";
                    return globalMapLanguage;
                }
                if (val.startsWith("fr")) {
                    globalMapLanguage = "fr";
                    return globalMapLanguage;
                }
            }
        }
    } catch (_) {}

    const urlLang = location?.pathname?.startsWith("/en") ? "en" : "fr";
    globalMapLanguage = urlLang;
    return globalMapLanguage;
};

export const UNIFIED_MAP_LIBRARIES = ["geometry", "marker", "places", "routes"];
